// import Vue from 'vue';

import * as Sentry from '@sentry/vue';

import {
  OPERATION,
  generateCRN,
  ecrNameVersion,
} from '../../../utils/worldLine';

const UNRESOLVED_CRNS_KEY = 'unresolvedCRNs';
// a CRN younger than this may still belong to a sale in flight, so the
// background sweep leaves it alone. Explicit passes (end of work day, the
// manual button) ignore the age and void everything queued.
const SWEEP_MIN_CRN_AGE_MS = 5 * 60 * 1000;
const SWEEP_INTERVAL_MS = 5 * 60 * 1000;
// session data is not loaded yet when the app mounts, so the boot pass waits
const BOOT_RECONCILE_DELAY_MS = 20 * 1000;
// how long we wait for the terminal to answer a single VOID before giving up
const VOID_REPLY_TIMEOUT_MS = 20 * 1000;
// spacing between VOIDs so the terminal is ready for the next command
const VOID_SPACING_MS = 3000;
const TERMINAL_WAIT_ATTEMPTS = 3;
const TERMINAL_WAIT_MS = 5000;
// attempts on a single CRN before we report it as stuck
const VOID_ATTEMPTS_BEFORE_REPORT = 5;

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const normalizeCRNEntry = entry => {
  // CRNs used to be stored as bare strings; keep reading those so kiosks
  // upgrading with a queue already in localStorage do not lose it
  const base =
    typeof entry === 'string' ? { CRN: entry } : entry && { ...entry };

  if (!base || typeof base.CRN !== 'string') {
    return null;
  }

  return {
    createdAt: null,
    attempts: 0,
    lastAttemptAt: null,
    orderNumber: null,
    ...base,
  };
};

const readStoredCRNs = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(UNRESOLVED_CRNS_KEY));
    if (!Array.isArray(stored)) {
      return [];
    }
    return stored.map(normalizeCRNEntry).filter(Boolean);
  } catch (error) {
    console.error('WORLDLINE: failed to read unresolved CRNs', error);
    return [];
  }
};

const persistCRNs = crns => {
  localStorage.setItem(UNRESOLVED_CRNS_KEY, JSON.stringify(crns));
};

// an entry without createdAt comes from the pre-metadata format, so it is by
// definition old enough to sweep
const isSweepable = entry =>
  !entry.createdAt || Date.now() - entry.createdAt >= SWEEP_MIN_CRN_AGE_MS;

export default {
  state: {
    websocketUrl: localStorage.getItem('posServerIp') || 'ws://127.0.0.1:8001',
    unresolvedCRNs: readStoredCRNs(),
    unresolvedCRNsStatus: 'idle', // 'idle', 'loading', 'pending'
    saleInProgress: false,
    sweepIntervalId: null,
    bootReconcileTimeoutId: null,
    onlineSweepListener: null,
    terminalSocket: null,
    terminalMessageCallback: null,
    terminalSocketConnected: false,
    terminalOnline: true,
    pendingHealthCheckCRNs: [],
    pendingSettlementCRN: null,
    settlementStatus: 'idle', // 'idle', 'loading'
  },
  mutations: {
    addUnresolvedCRN(state, payload) {
      const entry = normalizeCRNEntry(payload);

      if (!entry || state.unresolvedCRNs.some(item => item.CRN === entry.CRN)) {
        return;
      }

      state.unresolvedCRNs.push({
        ...entry,
        createdAt: entry.createdAt || Date.now(),
      });
      persistCRNs(state.unresolvedCRNs);
    },
    removeUnresolvedCRN(state, CRN) {
      state.unresolvedCRNs = state.unresolvedCRNs.filter(
        item => item.CRN != CRN
      );
      persistCRNs(state.unresolvedCRNs);
    },
    markUnresolvedCRNAttempt(state, CRN) {
      state.unresolvedCRNs = state.unresolvedCRNs.map(item =>
        item.CRN === CRN
          ? {
              ...item,
              attempts: (item.attempts || 0) + 1,
              lastAttemptAt: Date.now(),
            }
          : item
      );
      persistCRNs(state.unresolvedCRNs);
    },
    setSaleInProgress(state, inProgress) {
      state.saleInProgress = inProgress;
    },
    setSweepIntervalId(state, id) {
      state.sweepIntervalId = id;
    },
    setBootReconcileTimeoutId(state, id) {
      state.bootReconcileTimeoutId = id;
    },
    setOnlineSweepListener(state, listener) {
      state.onlineSweepListener = listener;
    },
    setTerminalSocket(state, socket) {
      state.terminalSocket = socket;
    },
    setTerminalSocketOnOpen(state, callback) {
      if (!state.terminalSocket) {
        return;
      }
      state.terminalSocket.onopen = callback;
    },
    setTerminalSocketOnMessage(state, callback) {
      if (!state.terminalSocket) {
        return;
      }
      state.terminalSocket.onmessage = callback;
    },
    setTerminalSocketOnClose(state, callback) {
      if (!state.terminalSocket) {
        return;
      }
      state.terminalSocket.onclose = callback;
    },
    setTerminalSocketOnError(state, callback) {
      if (!state.terminalSocket) {
        return;
      }
      state.terminalSocket.onerror = callback;
    },
    setTerminalMessageCallback(state, callback) {
      state.terminalMessageCallback = callback;
    },
    setTerminalSocketConnected(state, connected) {
      state.terminalSocketConnected = connected;
    },
    setTerminalOnline(state, online) {
      state.terminalOnline = online;
    },
    addPendingHealthCheckCRN(state, CRN) {
      state.pendingHealthCheckCRNs.push(CRN);
    },
    removePendingHealthCheckCRN(state, CRN) {
      state.pendingHealthCheckCRNs = state.pendingHealthCheckCRNs.filter(
        item => item !== CRN
      );
    },
    setUnresolvedCRNsStatus(state, status) {
      state.unresolvedCRNsStatus = status;
    },
    setPendingSettlementCRN(state, CRN) {
      state.pendingSettlementCRN = CRN;
    },
    setSettlementStatus(state, status) {
      state.settlementStatus = status;
    },
    setTerminalWebSocketUrl(state, url) {
      state.websocketUrl = url;
      localStorage.setItem('posServerIp', url);
    },
  },
  actions: {
    setSaleInProgress({ commit }, { inProgress }) {
      commit('setSaleInProgress', inProgress);
    },
    addUnresolvedCRN({ commit }, { CRN, orderNumber = null }) {
      commit('addUnresolvedCRN', { CRN, orderNumber });
    },
    removeUnresolvedCRN({ commit }, { CRN }) {
      commit('removeUnresolvedCRN', CRN);
    },
    async subscribeToPosTerminal(
      { commit, getters, dispatch },
      { force = false } = {}
    ) {
      const terminalSocket = getters.terminalSocket;
      const websocketUrl = getters.terminalWebSocketUrl;

      if (terminalSocket && !force) {
        if (terminalSocket.readyState === WebSocket.OPEN) {
          console.log('WORLDLINE: Already connected to terminal');
          commit('setTerminalSocketConnected', true);
          return Promise.resolve();
        }
        if (terminalSocket.readyState === WebSocket.CONNECTING) {
          console.log('WORLDLINE: Socket still connecting, waiting for open');
          return new Promise(resolve => {
            const onOpen = () => {
              terminalSocket.removeEventListener('open', onOpen);
              terminalSocket.removeEventListener('close', onClose);
              terminalSocket.removeEventListener('error', onError);
              resolve();
            };
            const onClose = () => {
              terminalSocket.removeEventListener('open', onOpen);
              terminalSocket.removeEventListener('close', onClose);
              terminalSocket.removeEventListener('error', onError);
              resolve();
            };
            const onError = () => {
              terminalSocket.removeEventListener('open', onOpen);
              terminalSocket.removeEventListener('close', onClose);
              terminalSocket.removeEventListener('error', onError);
              resolve();
            };
            terminalSocket.addEventListener('open', onOpen);
            terminalSocket.addEventListener('close', onClose);
            terminalSocket.addEventListener('error', onError);
          });
        }
      }

      return new Promise(resolve => {
        if (!websocketUrl) {
          console.log('WORLDLINE: No websocket URL provided');
          resolve();
          return;
        }

        const connection = new WebSocket(websocketUrl);

        commit('setTerminalSocket', connection);

        const onOpen = () => {
          console.log('WORLDLINE: Socket opened');
          commit('setTerminalSocketConnected', true);
          dispatch('closeToast');

          resolve();
        };
        const onMessage = event => {
          console.log('WORLDLINE: Socket message ----->>>', event);
          dispatch('handleTerminalPresenceFromRawMessage', { message: event });
        };
        const onClose = () => {
          console.log('WORLDLINE: Socket closed');
          dispatch('destroyPosTerminal');
          // A close without a preceding open/error (e.g. rejected handshake)
          // must still settle the promise, or awaiting callers hang.
          resolve();
        };
        const onError = event => {
          console.log('WORLDLINE: Socket error', event);
          dispatch('destroyPosTerminal');
          // dispatch('showToastInstantly', {
          //   title: Vue.i18n.translate(
          //     'kiosk.toasts.terminalConnectionError.title'
          //   ),
          //   description: Vue.i18n.translate(
          //     'kiosk.toasts.terminalConnectionError.description'
          //   ),
          //   isError: true,
          //   doNotClose: true,
          // });
          resolve();
        };

        commit('setTerminalSocketOnOpen', onOpen);
        commit('setTerminalSocketOnMessage', onMessage);
        commit('setTerminalSocketOnClose', onClose);
        commit('setTerminalSocketOnError', onError);

        // Re-attach the last message callback so a reconnect does not leave the
        // socket without the handler the current view registered.
        if (getters.terminalMessageCallback) {
          dispatch('setOnMessageCallback', {
            callback: getters.terminalMessageCallback,
          });
        }
      });
    },
    destroyPosTerminal({ state, commit }) {
      state?.terminalSocket?.close();
      commit('setTerminalSocket', null);
      commit('setTerminalSocketConnected', false);
      commit('setTerminalOnline', false);
    },
    sendPosTerminalMessage({ getters }, { data, onError }) {
      const terminalSocket = getters.terminalSocket;
      if (!terminalSocket || terminalSocket.readyState !== WebSocket.OPEN) {
        console.error(
          'WORLDLINE: Cannot send message, socket not open',
          terminalSocket?.readyState
        );
        if (onError) {
          onError();
        }
        return;
      }
      try {
        console.log('WORLDLINE: sending message', JSON.stringify(data));
        terminalSocket.send(JSON.stringify(data));
      } catch (error) {
        console.error('WORLDLINE: Error sending message', error);
        if (onError) {
          onError();
        }
      }
    },
    setOnMessageCallback({ commit, dispatch }, { callback }) {
      commit('setTerminalMessageCallback', callback);

      const wrappedCallback = async message => {
        const data = await dispatch('handleTerminalPresenceFromRawMessage', {
          message,
        });
        const shouldSkip = await dispatch('isHealthCheckResponse', { data });
        if (shouldSkip) {
          return;
        }
        if (callback) {
          await callback(message);
        }
      };
      commit('setTerminalSocketOnMessage', wrappedCallback);
    },
    handleTerminalPresenceFromRawMessage({ commit }, { message }) {
      let data = null;
      try {
        data = JSON.parse(message?.data);
      } catch (error) {
        return;
      }

      if (!data || typeof data !== 'object') {
        return;
      }

      // NP indicates the relay cannot reach the physical card terminal.
      if (data.responseCode === 'NP') {
        commit('setTerminalOnline', false);
        return;
      }

      const hasTerminalSignal =
        typeof data.operation === 'string' ||
        typeof data.responseCode === 'string' ||
        typeof data.result === 'boolean';

      if (hasTerminalSignal) {
        commit('setTerminalOnline', true);
      }

      return data;
    },
    isHealthCheckResponse({ getters, commit }, { data }) {
      const CRN = data?.CRN;
      if (!CRN) {
        return false;
      }

      const isHealthCheck = getters.pendingHealthCheckCRNs.includes(CRN);
      if (!isHealthCheck) {
        return false;
      }

      commit('removePendingHealthCheckCRN', CRN);
      return true;
    },
    probeTerminalHealth({ getters, dispatch, commit }) {
      if (!getters.terminalSocketConnected || getters.terminalOnline) {
        return;
      }

      const CRN = generateCRN();
      commit('addPendingHealthCheckCRN', CRN);

      // FOR PROBING TERMINAL HEALTH WE ARE SENDING A VOID OPERATION WITH A NEW CRN TO AVOID ANY OTHER OPERATIONS BEING EXECUTED.
      dispatch('sendPosTerminalMessage', {
        data: {
          CRN,
          operation: OPERATION.VOID,
        },
      });

      setTimeout(() => {
        commit('removePendingHealthCheckCRN', CRN);
      }, 5000);
    },
    setTerminalOffline({ commit, getters }) {
      const terminalSocket = getters.terminalSocket;
      terminalSocket?.close();
      commit('setTerminalSocketConnected', false);
      commit('setTerminalOnline', false);
    },
    async voidUnresolvedCRNs(
      { getters, dispatch, commit },
      { onVoided, force = true, reason = 'manual' } = {}
    ) {
      const finish = status => {
        commit('setUnresolvedCRNsStatus', status);
        if (onVoided) {
          onVoided();
        }
      };

      // the background sweep can coincide with an explicit pass
      if (getters.loadingUnresolvedCRNs) {
        console.log('WORLDLINE: a void pass is already running');
        if (onVoided) {
          onVoided();
        }
        return;
      }

      // a void pass owns the terminal for its duration, so it must never run
      // while a customer is paying. Explicit passes are driven by staff or by
      // closing time, when no sale is in flight.
      if (!force && getters.saleInProgress) {
        console.log(`WORLDLINE: sale in progress, void pass skipped (${reason})`);
        if (onVoided) {
          onVoided();
        }
        return;
      }

      const queue = getters.unresolvedCRNs.filter(
        entry => force || isSweepable(entry)
      );

      if (!queue.length) {
        finish(getters.hasUnresolvedCRNs ? 'pending' : 'idle');
        return;
      }

      commit('setUnresolvedCRNsStatus', 'loading');

      // the printer is deliberately NOT required here: reversing the charge
      // matters more than the cardholder receipt
      const terminalReady = await dispatch('waitForTerminal');

      if (!terminalReady) {
        console.warn(
          `WORLDLINE: terminal unavailable, void pass deferred (${reason})`
        );
        dispatch('reportUnresolvedCRNs', {
          reason: `${reason}:terminal-unavailable`,
        });
        finish('pending');
        return;
      }

      const previousCallback = getters.terminalMessageCallback;

      try {
        for (let i = 0; i < queue.length; i++) {
          if (!getters.terminalOnline) {
            console.log('WORLDLINE: Terminal offline in voidUnresolvedCRNs');
            break;
          }

          const CRN = queue[i].CRN;
          commit('markUnresolvedCRNAttempt', CRN);

          const outcome = await dispatch('voidCRNAndAwaitReply', {
            CRN,
            previousCallback,
          });

          // NF means the terminal has no such transaction in its journal, so
          // there is nothing left to reverse — as resolved as a real void
          if (outcome === 'voided' || outcome === 'not_found') {
            commit('removeUnresolvedCRN', CRN);
          } else {
            console.warn(`WORLDLINE: VOID ${CRN} ended as ${outcome}`);
          }

          if (i < queue.length - 1) {
            await wait(VOID_SPACING_MS);
          }
        }
      } finally {
        // hand the terminal back to whichever view owned it
        dispatch('setOnMessageCallback', { callback: previousCallback });

        const remaining = getters.hasUnresolvedCRNs;
        if (remaining) {
          dispatch('reportUnresolvedCRNs', { reason });
        }
        finish(remaining ? 'pending' : 'idle');
      }
    },
    // Sends one VOID and resolves only once the terminal answers for that CRN,
    // so clearing the queue no longer depends on which view is mounted.
    voidCRNAndAwaitReply({ dispatch, getters }, { CRN, previousCallback }) {
      return new Promise(resolve => {
        let settled = false;

        const finish = outcome => {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(timeoutId);
          resolve(outcome);
        };

        const timeoutId = setTimeout(
          () => finish('timeout'),
          VOID_REPLY_TIMEOUT_MS
        );

        dispatch('setOnMessageCallback', {
          callback: async message => {
            let data = null;
            try {
              data = JSON.parse(message?.data);
            } catch (error) {
              return;
            }

            if (data?.operation !== OPERATION.VOID || data?.CRN !== CRN) {
              // not ours — hand it to whoever owned the socket before us so no
              // terminal reply is swallowed by the void pass
              if (previousCallback) {
                await previousCallback(message);
              }
              return;
            }

            if (!data.result) {
              finish(data.responseCode === 'NF' ? 'not_found' : 'declined');
              return;
            }

            if (getters.printerIsOnline && data.cardholderReceipt) {
              dispatch('printReceipt', { text: data.cardholderReceipt });
            }

            try {
              await dispatch('updateInPersonPaymentIntent', {
                type: 'payment_voided',
                data,
              });
            } catch (error) {
              console.error(
                'WORLDLINE: failed to report the void to the backend',
                error
              );
            }

            finish('voided');
          },
        });

        dispatch('sendPosTerminalMessage', {
          data: {
            CRN,
            operation: OPERATION.VOID,
            reversalType: 'autoVoid',
            dlgDisabled: false,
            receiptLineLength: 40,
          },
          onError: () => finish('send_failed'),
        });
      });
    },
    async waitForTerminal(
      { getters, dispatch },
      { attempts = TERMINAL_WAIT_ATTEMPTS } = {}
    ) {
      for (let attempt = 0; attempt < attempts; attempt++) {
        await dispatch('subscribeToPosTerminal');

        if (getters.terminalSocketConnected && getters.terminalOnline) {
          return true;
        }

        dispatch('probeTerminalHealth');
        await wait(TERMINAL_WAIT_MS);
      }

      return getters.terminalSocketConnected && getters.terminalOnline;
    },
    reportUnresolvedCRNs({ getters }, { reason } = {}) {
      const stuck = getters.unresolvedCRNs.filter(
        entry => (entry.attempts || 0) >= VOID_ATTEMPTS_BEFORE_REPORT
      );

      if (!stuck.length) {
        return;
      }

      console.error(
        'WORLDLINE: unresolved CRNs still open after repeated voids',
        stuck
      );

      // a charge nobody managed to reverse must be visible without opening
      // the kiosk it happened on
      Sentry.captureMessage('WORLDLINE: unresolved CRNs could not be voided', {
        level: 'error',
        extra: {
          reason,
          restaurantId: getters.getCurrentRestaurantId,
          CRNs: stuck,
        },
      });
    },
    startKioskPaymentRecovery({ state, getters, dispatch, commit }) {
      if (!getters.isKiosk || state.sweepIntervalId) {
        return;
      }

      const sweep = reason => {
        if (!getters.hasUnresolvedCRNs) {
          return;
        }
        // age-gated so a sale still in flight is never voided under it
        dispatch('voidUnresolvedCRNs', { force: false, reason });
      };

      // the end of work day pusher event is not replayed, so a kiosk that was
      // offline or restarting at closing time would otherwise never reconcile
      const bootTimeoutId = setTimeout(() => {
        commit('setBootReconcileTimeoutId', null);
        dispatch('reconcileEndOfWorkDay');
      }, BOOT_RECONCILE_DELAY_MS);
      commit('setBootReconcileTimeoutId', bootTimeoutId);

      const onlineListener = () => sweep('online');
      window.addEventListener('online', onlineListener);
      commit('setOnlineSweepListener', onlineListener);

      const intervalId = setInterval(() => sweep('interval'), SWEEP_INTERVAL_MS);
      commit('setSweepIntervalId', intervalId);
    },
    stopKioskPaymentRecovery({ state, commit }) {
      if (state.sweepIntervalId) {
        clearInterval(state.sweepIntervalId);
        commit('setSweepIntervalId', null);
      }
      if (state.bootReconcileTimeoutId) {
        clearTimeout(state.bootReconcileTimeoutId);
        commit('setBootReconcileTimeoutId', null);
      }
      if (state.onlineSweepListener) {
        window.removeEventListener('online', state.onlineSweepListener);
        commit('setOnlineSweepListener', null);
      }
    },
    async reconcileEndOfWorkDay({ getters, dispatch }) {
      if (!getters.isKiosk) {
        return;
      }

      const outsideWorkingHours = getters.restaurantInWorkingHours === false;
      const settlementPending = Boolean(getters.pendingSettlementCRN);

      if (
        outsideWorkingHours &&
        (settlementPending || getters.hasUnresolvedCRNs)
      ) {
        await dispatch('settleEndOfWorkDay');
        return;
      }

      if (getters.hasUnresolvedCRNs) {
        await dispatch('voidUnresolvedCRNs', { force: false, reason: 'boot' });
      }
    },
    voidPayment({ dispatch }, { CRN }) {
      const data = {
        CRN,
        operation: OPERATION.VOID,
        reversalType: 'autoVoid',
        dlgDisabled: false,
        receiptLineLength: 40,
      };

      dispatch('sendPosTerminalMessage', { data });
    },
    async settleEndOfWorkDay({ dispatch, getters, commit }) {
      const isKiosk = getters.isKiosk;

      if (!isKiosk) {
        return;
      }

      await dispatch('subscribeToPosTerminal');
      try {
        await dispatch('voidUnresolvedCRNs', { reason: 'end-of-work-day' });
      } catch (error) {
        // settlement still has to happen, but the CRNs stay queued and are
        // reported so an open charge is not lost behind a settled terminal
        console.error(
          'WORLDLINE: Failed to resolve unresolved CRNs before settlement.',
          error
        );
        dispatch('reportUnresolvedCRNs', { reason: 'end-of-work-day:failed' });
      }

      const pendingSettlementCRN = getters.pendingSettlementCRN;
      const newCRN = generateCRN();
      const CRN = pendingSettlementCRN || newCRN;

      const data = {
        operation: OPERATION.SETTLEMENT,
        ecrNameVersion,
        CRN,
        dlgDisabled: false,
        receiptLineLength: 40,
      };

      commit('setPendingSettlementCRN', CRN);
      commit('setSettlementStatus', 'loading');

      const onmessage = async message => {
        const data = JSON.parse(message.data);
        if (data.operation === OPERATION.SETTLEMENT && data.result) {
          dispatch('saveSettlement', {
            data,
          });

          const onPrintSuccess = () => {
            commit('setPendingSettlementCRN', null); // IMPORTANT to reset the CRN after a successful settlement
            commit('setSettlementStatus', 'idle');

            dispatch('generateNewKioskSession');
          };
          const onPrinterError = () => {
            commit('setSettlementStatus', 'idle');
          };

          dispatch('printReceipt', {
            text: data.merchantReceipt,
            withLogo: false,
            onSuccess: onPrintSuccess,
            onError: onPrinterError,
          });

          return;
        } else if (!data.result) {
          commit('setSettlementStatus', 'idle');
        }
      };

      dispatch('setOnMessageCallback', { callback: onmessage });
      dispatch('sendPosTerminalMessage', { data });
    },
  },
  getters: {
    saleInProgress: state => state.saleInProgress,
    unresolvedCRNs: state => state.unresolvedCRNs,
    unresolvedCRNValues: state => state.unresolvedCRNs.map(entry => entry.CRN),
    hasUnresolvedCRNs: state => state.unresolvedCRNs.length > 0,
    loadingUnresolvedCRNs: state => state.unresolvedCRNsStatus === 'loading',
    terminalSocketConnected: state => state.terminalSocketConnected,
    terminalOnline: state => state.terminalOnline,
    pendingHealthCheckCRNs: state => state.pendingHealthCheckCRNs,
    terminalSocket: state => state.terminalSocket,
    terminalMessageCallback: state => state.terminalMessageCallback,
    pendingSettlementCRN: state => state.pendingSettlementCRN,
    loadingPendingSettlement: state => state.settlementStatus === 'loading',
    terminalWebSocketUrl: state => state.websocketUrl,
  },
};
